import { useEffect, useMemo, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/app/Bits";
import BrandHero from "@/components/app/BrandHero";
import EmptyDogState from "@/components/app/EmptyDogState";
import { ChevronLeft, ChevronRight, Sparkles, Trophy, Award, CalendarCheck, Scissors, Dumbbell, Activity, GraduationCap } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend
} from "recharts";

function MetricCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <Card className="border-border shadow-none rounded-lg hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            <p className="font-display text-4xl font-extrabold tracking-tighter mt-2">{value}</p>
            {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
          </div>
          <div className="h-9 w-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function YearInReviewPage() {
  const { activeDog } = useDogs();
  // default to last year, since "Jan 1" view typically looks back
  const [year, setYear] = useState(() => {
    const now = new Date();
    return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeDog) return;
    (async () => {
      const res = await api.get("/year-summary", { params: { dog_id: activeDog.id, year } });
      setData(res.data);
    })();
  }, [activeDog, year]);

  const barData = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Grooming", value: data.grooming_count },
      { name: "Training", value: data.training_count },
      { name: "Exercise", value: data.exercise_count },
      { name: "Line brush", value: data.line_brush_count },
      { name: "Webinars", value: data.webinars_count },
    ];
  }, [data]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Grooming", value: data.grooming_count },
      { name: "Training", value: data.training_count },
      { name: "Exercise", value: data.exercise_count },
    ].filter((d) => d.value > 0);
  }, [data]);

  const showsByMonth = useMemo(() => {
    if (!data?.shows_by_month) return [];
    const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return data.shows_by_month.map((m) => ({
      month: labels[(m.month || 1) - 1],
      shows: m.shows,
      points: m.points,
      titles: m.titles,
    }));
  }, [data]);

  const pieColors = ["hsl(120 13% 20%)", "hsl(36 25% 55%)", "hsl(17 41% 44%)"];

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  return (
    <div className="space-y-8" data-testid="year-review-page">
      <BrandHero
        overline="Year in review"
        title={`${activeDog.name} · ${year}`}
        description={(<>Cumulative stats for the whole year — open this each January 1 for a clean recap before planning the season ahead.</>)}
        action={(
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setYear((y) => y - 1)} data-testid="year-prev"><ChevronLeft size={16} /></Button>
            <Button variant="outline" size="sm" onClick={() => setYear(new Date().getFullYear())} data-testid="year-this">Current</Button>
            <Button variant="outline" size="sm" onClick={() => setYear((y) => y + 1)} data-testid="year-next"><ChevronRight size={16} /></Button>
          </div>
        )}
        testid="year-review-hero"
      />

      {/* Jan 1 banner */}
      <Card className="border-border shadow-none rounded-lg bg-secondary">
        <CardContent className="p-6 flex items-start gap-4">
          <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">A look back at {year}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Cumulative stats for the whole year — open this each January 1 for a clean recap before planning the season ahead.
            </p>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Top metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            <MetricCard testid="yr-points" icon={Trophy} label="Show points" value={data.show_points} hint="cumulative" />
            <MetricCard testid="yr-titles" icon={Award} label="New titles" value={data.new_titles} hint="earned this year" />
            <MetricCard testid="yr-shows" icon={CalendarCheck} label="Shows attended" value={data.shows_attended} hint="entries logged" />
            <MetricCard testid="yr-webinars" icon={GraduationCap} label="Webinars watched" value={data.webinars_count} hint="Leading Edge Academy" />
            <MetricCard testid="yr-grooming" icon={Scissors} label="Grooming" value={data.grooming_count} hint={`${data.grooming_events} events · ${data.grooming_prompts} prompts`} />
            <MetricCard testid="yr-training" icon={Dumbbell} label="Training" value={data.training_count} hint={`${data.stacking_count} stacking · ${data.gait_count} gait`} />
            <MetricCard testid="yr-exercise" icon={Activity} label="Exercise" value={data.exercise_count} hint="weekly prompts done" />
            <MetricCard testid="yr-line-brush" icon={Sparkles} label="Line brush days" value={data.line_brush_count} hint="daily prompts" />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Activity by category</p>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Training mix</p>
                <div className="h-72">
                  {pieData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No activity logged for {year}</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                          {pieData.map((slice, i) => <Cell key={slice.name} fill={pieColors[i % pieColors.length]} />)}
                        </Pie>
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Shows by month — only render if there's at least one show this year */}
          {data.shows_attended > 0 ? (
            <Card className="border-border shadow-none rounded-lg" data-testid="yr-shows-by-month">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">🎀 Shows by month</p>
                <p className="text-sm text-muted-foreground mb-4">When you were in the ring, plus points earned each month.</p>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={showsByMonth}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="left" dataKey="shows" name="Shows" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="points" name="Points" fill="hsl(43 60% 50%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Quick show + webinar lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-0">
                <p className="font-medium px-6 py-4 border-b border-border">Shows in {year}</p>
                {data.show_results.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-6">No shows logged.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.show_results.map((r) => (
                      <li key={r.id} className="px-6 py-3 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{r.show_name}</p>
                          <p className="text-xs text-muted-foreground">{r.show_date}{r.placement ? ` · ${r.placement}` : ""}{r.title_earned ? " · 🏅 title" : ""}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{r.points || "—"} pts</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-0">
                <p className="font-medium px-6 py-4 border-b border-border">Webinars in {year}</p>
                {data.webinars.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-6">No webinars logged.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.webinars.map((w) => (
                      <li key={w.id} className="px-6 py-3 text-sm">
                        <p className="font-medium">{w.webinar_name}</p>
                        <p className="text-xs text-muted-foreground">{w.watched_date}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
